import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { Glossary } from "src/components/Text/Glossary";
import { ParentGuardianSection } from "./ParentGuardianSection";
import {
  StudentAddress,
  useAddGuardiansToStudent,
} from "./useAddGuardiansToStudent";
import { useGuardianEntries } from "./useGuardianEntries";

interface AddGuardiansDialogProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  studentId: string;
  studentName: string;
  studentAddress: StudentAddress;
  // Guardian ids already linked to the student, excluded from search.
  excludeIds: string[];
}

export function AddGuardiansDialog({
  isOpen,
  onClose,
  organizationId,
  studentId,
  studentName,
  studentAddress,
  excludeIds,
}: AddGuardiansDialogProps) {
  const guardians = useGuardianEntries();
  const { submit, isSubmitting } = useAddGuardiansToStudent({
    organizationId,
    studentId,
    studentAddress,
  });

  const handleAdd = async () => {
    amplitude.track("Add relationship clicked");
    const added = await submit(guardians.entries);
    if (added) onClose();
  };

  // An open (unconfirmed) new guardian sub-form must be added or cancelled first.
  const hasOpenGuardianDraft = guardians.entries.some(
    (entry) => entry.kind === "new" && !entry.confirmed
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Glossary>Add parent/guardian</Glossary>
        </ModalHeader>
        <ModalBody>
          <ParentGuardianSection
            organizationId={organizationId}
            entries={guardians.entries}
            studentName={studentName}
            excludeIds={excludeIds}
            disablePortal
            onAddExisting={guardians.addExisting}
            onAddDraft={guardians.addDraft}
            onUpdateDraft={guardians.updateDraft}
            onConfirmDraft={guardians.confirmDraft}
            onRemove={guardians.remove}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <Button
            type="button"
            variant="ghost"
            colorScheme="gray"
            onClick={() => {
              amplitude.track("Cancel relationship clicked");
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            isDisabled={guardians.entries.length === 0 || hasOpenGuardianDraft}
            isLoading={isSubmitting}
            onClick={handleAdd}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
